import type {
  Configuration,
  SurfaceTypesMapValue,
  SurfaceMaps,
  SurfaceOptionsGroup,
} from '~/types/configurator'

export default function useConfigurationOptionalBaseParts(
  configuration: Ref<Configuration | null> = ref(null),
  currentValues: ComputedRef<Configuration['values']>,
  values: Ref<Record<string, Configuration['values'][number]>>,
  surfaceTypes: ComputedRef<Map<number, SurfaceTypesMapValue>>,
  surfaceMaps: ComputedRef<SurfaceMaps>,
  surfaceOptionsGroups: ComputedRef<Map<string, SurfaceOptionsGroup>>
) {
  // optional base parts are not included as metas or accessories - they are tied to the surface option - if it is optional, then it is optional base part
  const optionalBaseParts = computed(() => {
    if (!configuration.value) {
      return new Map<
        number,
        {
          surfaceOption: Configuration['surfacesOptions'][number]
          basePartDeselected: boolean
        }
      >()
    }

    return configuration.value?.surfacesOptions
      .filter((option) => option.optional)
      .reduce(
        (acc, option) => {
          // find corresponding value in values
          const correspondingValue = configuration.value?.values.find(
            (value) => value.partCategoryNid === option.partCategoryNid
          )

          // if not found, return acc
          if (!correspondingValue) {
            return acc
          }

          // set boolean to whatever is in value
          acc.set(option.partCategoryNid, {
            surfaceOption: option,
            basePartDeselected: correspondingValue.basePartDeselected,
          })
          return acc
        },
        new Map<
          number,
          {
            surfaceOption: Configuration['surfacesOptions'][number]
            basePartDeselected: boolean
          }
        >()
      )
  })

  function toggleOptionalBasePart(partCategoryNid: number) {
    const optionalBasePart = optionalBaseParts.value.get(partCategoryNid)

    if (!optionalBasePart) {
      return
    }

    if (!values.value[partCategoryNid]) {
      return
    }

    if (!optionalBasePart.basePartDeselected) {
      // remove optional base part (not really but update the value to reflect it)
      values.value[partCategoryNid].basePartDeselected = true
      values.value[partCategoryNid].surfaceNid = null
      values.value[partCategoryNid].surfaceTranslationOfNid = null
      values.value[partCategoryNid].surfaceTitle = null
      values.value[partCategoryNid].surfaceImageSrc = null
      values.value[partCategoryNid].partTitle =
        optionalBasePart.surfaceOption.emptyOptionTitle
    } else {
      // add optional base part (also not really but update the value to reflect it)
      values.value[partCategoryNid].basePartDeselected = false

      if (optionalBasePart.surfaceOption.surfaceCollectionNids[0]) {
        let surface:
          | Configuration['surfaceTypes'][number]['surfaceCategories'][number]['surfaceCollections'][number]['surfaces'][number]
          | undefined

        const surfaceCollectionNid =
          optionalBasePart.surfaceOption.surfaceCollectionNids[0]

        const { type, collection } =
          configuratorUtils.findSurfaceCollectionByNid(
            surfaceTypes.value,
            surfaceMaps.value,
            surfaceCollectionNid
          )

        if (
          optionalBasePart.surfaceOption.surfacesInheritedFromProduct &&
          type &&
          type.icon?.fileName
        ) {
          // inherited surface collections from product - can be grouped - try use the first surface used in the group
          const key = type.icon.fileName
          const surfaceOptionGroup = surfaceOptionsGroups.value.get(key)
          const firstOptionInGroup = surfaceOptionGroup?.surfaceOptionGroups
            .get(type.nid)
            ?.surfaceOptions.at(0)
          if (firstOptionInGroup?.partCategoryNid) {
            const valueForOption = configuratorUtils.findValue(
              currentValues.value,
              firstOptionInGroup?.partCategoryNid,
              firstOptionInGroup?.accessoryNid
            )
            if (valueForOption && valueForOption.surfaceNid) {
              surface = configuratorUtils.findSurfaceByNid(
                surfaceTypes.value,
                surfaceMaps.value,
                valueForOption.surfaceNid
              ).surface
            }
          }
        }

        if (!surface && collection) {
          // as fall back take the first surface from the the first collection
          surface = collection.surfaces.values().next().value
        }

        if (surface) {
          values.value[partCategoryNid].surfaceImageSrc = surface.image?.url
          values.value[partCategoryNid].surfaceNid = surface.nid
          values.value[partCategoryNid].surfaceTitle = surface.title
          values.value[partCategoryNid].surfaceTranslationOfNid =
            surface.translationOfNid
        }
      }

      values.value[partCategoryNid].partTitle =
        optionalBasePart.surfaceOption.title
    }
  }

  return {
    optionalBaseParts: optionalBaseParts,
    toggleOptionalBasePart,
  }
}
